import React, { useLayoutEffect, useRef } from 'react'
import styled from 'styled-components'
import { Icon, IconProps } from '../Icon'
import { CSSCursor } from '../types/css'
import { BaseProps } from '../types/props'
import { overloadColor } from '../util/style'

interface WrapperProps {
  inline: boolean
  vertical: boolean

  cursor: CSSCursor
}
const Wrapper = styled.div<WrapperProps>`
  display: ${props => (props.inline ? 'inline-flex' : 'flex')};
  align-items: center;
  justify-content: center;

  ${props => props.vertical && 'flex-direction: column;'}

  cursor: ${props => props.cursor};

  label {
    cursor: ${props => props.cursor};
  }
`
interface BoxProps {
  checked: boolean
  indeterminate: boolean

  width: string
  height: string
  color: string
  background: string
  borderRadius: string
}
const Box = styled.div<BoxProps>`
  position: relative;

  width: ${props => props.width};
  height: ${props => props.height};

  border-radius: ${props => props.borderRadius};
  border: 1px solid ${props => overloadColor(props.color)};

  background-color: ${props =>
    props.checked || props.indeterminate
      ? overloadColor(props.color)
      : overloadColor(props.background)};

  i {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`
function getLabelPositionCSS(position: CheckboxLabelPosition, margin: string) {
  switch (position) {
    case 'right':
      return `
        margin-left: ${margin};
      `
    case 'left':
      return `
        order: -1;
        margin-right: ${margin};
      `
    case 'above':
      return `
        order: -1;
        margin-bottom: ${margin};
      `
    case 'below':
      return `
        margin-top: ${margin};
      `
  }
}
interface LabelProps {
  position: CheckboxLabelPosition

  fontSize: string
  color: string
  margin: string
}
const Label = styled.label<LabelProps>`
  ${props => getLabelPositionCSS(props.position, props.margin)}

  font-size: ${props => props.fontSize};
  color: ${props => overloadColor(props.color)};
`

export type CheckboxLabelPosition = 'right' | 'left' | 'above' | 'below'

export interface CheckboxProps extends BaseProps {
  checked: boolean
  indeterminate?: boolean

  tabIndex?: number
  disabled?: boolean
  inline?: boolean

  label?: string
  labelPosition?: CheckboxLabelPosition
  labelMargin?: string
  indeterminateIconProps?: Partial<IconProps>
  checkedIconProps?: Partial<IconProps>

  cursor?: CSSCursor
  fontSize?: string
  /** Sets color of label */
  color?: string

  /** Sets both width and height of box */
  boxSize?: string
  /** Sets width of box, overrides boxSize */
  boxWidth?: string
  /** Sets height of box, overrides boxSize */
  boxHeight?: string
  /** Sets border color and, if checked/indeterminate, background color of box */
  boxColor?: string
  /** Sets background color of box when unchecked */
  boxBackground?: string
  boxBorderRadius?: string

  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  onChange: (checked: boolean) => void
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      id,

      checked,
      indeterminate = false,

      tabIndex = 0,
      disabled = false,
      inline = true,

      label,
      labelPosition = 'right',
      labelMargin = '6px',
      indeterminateIconProps = {},
      checkedIconProps = {},

      cursor,
      fontSize = '1em',
      color = 'text.dark',

      boxSize = '22px',
      boxWidth,
      boxHeight,
      boxColor = 'primary',
      boxBackground = 'white',
      boxBorderRadius = '4px',

      onKeyDown,
      onClick,
      onChange,
    }: CheckboxProps,
    ref
  ) => {
    const checkboxRef = useRef<HTMLInputElement | null>(null)

    useLayoutEffect(() => {
      if (!checkboxRef.current) return
      checkboxRef.current.indeterminate = indeterminate
    }, [indeterminate])

    function emitChange(value: boolean) {
      if (disabled) return
      onChange(value)
    }

    function handleKeyDown(evt: React.KeyboardEvent<HTMLDivElement>) {
      onKeyDown?.(evt)
      if (evt.defaultPrevented) return

      if (evt.key === 'Enter' || evt.key === ' ') emitChange(!checked)
    }
    function handleClick(evt: React.MouseEvent<HTMLDivElement>) {
      onClick?.(evt)
      if (evt.defaultPrevented) return

      emitChange(!checked)
    }

    const iconProps = indeterminate ? indeterminateIconProps : checkedIconProps

    return (
      <Wrapper
        ref={ref}
        className={className}
        id={id}
        tabIndex={disabled ? undefined : tabIndex}
        inline={inline}
        vertical={labelPosition === 'above' || labelPosition === 'below'}
        cursor={cursor ?? (disabled ? 'default' : 'pointer')}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      >
        <input
          ref={checkboxRef}
          hidden
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={() => void 0}
        />
        <Box
          className={
            '--checkbox-box' + (checked ? ' --checkbox-box-checked' : '')
          }
          checked={checked}
          indeterminate={indeterminate}
          width={boxWidth ?? boxSize}
          height={boxHeight ?? boxSize}
          color={boxColor}
          background={boxBackground}
          borderRadius={boxBorderRadius}
        >
          {checked || indeterminate ? (
            <Icon
              className="--checkbox-box-icon"
              icon={indeterminate ? 'minus' : 'check'}
              size="0.8rem"
              translateY="1px"
              color="white"
              {...iconProps}
            />
          ) : (
            <i></i>
          )}
        </Box>
        {label && (
          <Label
            className="--checkbox-label"
            position={labelPosition}
            fontSize={fontSize}
            color={color}
            margin={labelMargin}
          >
            {label}
          </Label>
        )}
      </Wrapper>
    )
  }
)
